import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import Papa from 'papaparse';
import { DateTime } from 'luxon';

import Sheet from 'components/core/Sheet';
import Typography from 'components/core/Typography';
import Stack from 'components/core/Stack';
import Button from 'components/core/Button';
import Grid from 'components/core/Grid';
import Autocomplete from 'components/core/Autocomplete';
import ProcessingOverlay from 'components/core/ProcessingOverlay';

import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';

import ImportedObservations from 'components/ImportedObservations';

import Observation from 'domain/Observation';

import observationActions from 'actions/observationActions';
import personActions from 'actions/personActions';
import lookupTableActions from 'actions/lookupTableActions';
import noteActions from 'actions/noteActions';

const parseDate = (value) => {
    if (!value || value === '') return null;
    
    const trimmedValue = String(value).trim();
    
    if (/^\d+$/.test(trimmedValue)) {
        const excelDate = parseInt(trimmedValue);
        const epoch = DateTime.fromObject({ year: 1900, month: 1, day: 1 });
        const daysToAdd = excelDate > 59 ? excelDate - 2 : excelDate - 1;
        return epoch.plus({ days: daysToAdd }).toJSDate();
    }
    
    let dt = DateTime.fromISO(trimmedValue);
    if (dt.isValid) {
        return dt.toJSDate();
    }
    
    dt = DateTime.fromSQL(trimmedValue);
    if (dt.isValid) {
        return dt.toJSDate();
    }
    
    const jsDate = new Date(trimmedValue);
    if (!isNaN(jsDate.getTime())) {
        return jsDate;
    }
    
    return null;
};

const classMap = {
    'Name': 'names',
    'Substantiation': 'substantiations',
    'LocDeterminization': 'locDeterminization',
    'Substrate': 'substrates',
    'ObservationQuality': 'qualities',
    'Location': 'locations',
    'Person': 'persons',
    'Event': 'events',
    'SourceOwned': 'sourceOwneds',
    'Repository': 'repositories',
    'NoteType': 'noteTypes',
    'EvidenceFormat': 'evidenceFormats',
    'Town': 'towns',
    'Keyword': 'keywords',
}

const fieldsToMap = Observation.FIELDS.filter(field => field.field !== 'label');

const mappingDefaults = fieldsToMap.reduce((acc, field) => {
    acc[field.field] = null;
    return acc;
}, {});

const ObservationImport = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const observationOptions = useSelector(({ lookupTable }) => lookupTable.options);
    const personList = useSelector(({ person }) => person.list);
    const [uploadData, setUploadData] = useState([]);
    const [uploadFields, setUploadFields] = useState([]);
    const [mapping, setMapping] = useState(mappingDefaults);
    const [observations, setObservations] = useState([]);
    const [erroredObservations, setErroredObservations] = useState([]);
    const [expanded, setExpanded] = useState('upload');

    const [isUploadComplete, setIsUploadComplete] = useState(false);
    const [isMappingComplete, setIsMappingComplete] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const observationNoteTypes = useMemo(() => {
        return observationOptions.noteTypes?.filter(noteType => 
            noteType.get('filteredFor') === 'Observation'
        ) || [];
    }, [observationOptions.noteTypes]);

    const fieldsToMapWithNotes = useMemo(() => {
        return [
            ...fieldsToMap,
            ...observationNoteTypes.map(noteType => ({
                field: `note_${noteType.id}`,
                label: `Note: ${noteType.get('label')}`,
                type: 'Note'
            }))
        ];
    }, [observationNoteTypes]);

    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
        dispatch(personActions.list());
    }, [dispatch]);

    useEffect(() => {
        if (observationNoteTypes.length > 0) {
            setMapping(prev => {
                const newMapping = { ...prev };
                observationNoteTypes.forEach(noteType => {
                    const fieldKey = `note_${noteType.id}`;
                    if (!(fieldKey in newMapping)) {
                        newMapping[fieldKey] = null;
                    }
                });
                return newMapping;
            });
        }
    }, [observationNoteTypes]);



    useEffect(() => {
        if (isUploadComplete && isMappingComplete) {
            setExpanded('review');
        } else if (isUploadComplete) {
            setExpanded('mapping');
        } else {
            setExpanded('upload');
        }
    }, [isUploadComplete, isMappingComplete]);

    useEffect(() => {
        if (isMappingComplete && observations.length === 0) {
            handleReview();
        }
    }, [isMappingComplete, mapping]);

    const normalizeString = (str) => {
        return str.toLowerCase().replace(/[^a-z0-9]/g, '');
    };


    const handleChangeAccordion = (panel) => {
        const pannelCriteria = {
            upload: [],
            mapping: [isUploadComplete],
            review: [isUploadComplete, isMappingComplete]
        }

        if (pannelCriteria[panel].every((c) => c)) {
            return (_, isExpanded) => {
                setExpanded(isExpanded ? panel : false);
            }
        }
    }

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    setUploadData(results.data);
                    setUploadFields(results.meta.fields);
                    attemptMapping(results.meta.fields);
                    setIsUploadComplete(true);
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                }
            });
        }
    };

    const renderUpload = () => {
        return (
            <Stack spacing={2}>
                <Button component="label">
                    Select CSV File
                    <input
                        type="file"
                        hidden
                        accept=".csv"
                        onChange={handleFileUpload}
                    />
                </Button>
                {uploadFields.length > 0 && (
                    <Typography>
                        File uploaded successfully with {uploadFields.length} columns
                    </Typography>
                )}
            </Stack>
        )

    }

    const attemptMapping = (newUploadFields) => {
        if (!newUploadFields.length) {
            return;
        }
    
        const newMapping = {...mapping};
        const normalizedUploadFields = newUploadFields.map(normalizeString);
    
        let isNewMappingComplete = false;
        fieldsToMap.forEach((field) => {
            const normalizedField = normalizeString(field.field);
            const normalizedLabel = normalizeString(field.label);
            
            const fieldIndex = normalizedUploadFields.findIndex(f => f === normalizedField);
            const labelIndex = normalizedUploadFields.findIndex(f => f === normalizedLabel);
            
            if (fieldIndex !== -1) {
                newMapping[field.field] = newUploadFields[fieldIndex];
            } else if (labelIndex !== -1) {
                newMapping[field.field] = newUploadFields[labelIndex];
            }
        });
    
        setMapping(newMapping);
        if (Object.values(newMapping).every((v) => v !== null)) {
            isNewMappingComplete = true;
        }

        if (isNewMappingComplete !== isMappingComplete) {
            setIsMappingComplete(isNewMappingComplete);
        }
    }

    const handleChangeMapping = (field, value) => {
        let isNewMappingComplete = false;
        const newMapping = {
            ...mapping,
            [field]: value
        };
        setMapping(newMapping);
        if (Object.values(newMapping).every((v) => v !== null)) {
            isNewMappingComplete = true;
        }

        if (isNewMappingComplete !== isMappingComplete) {
            setIsMappingComplete(isNewMappingComplete);
        }
    }

    const renderFieldMappingWarnings = () => {
        const requiredFields = ['name', 'observedBy'];
        const missingRequired = requiredFields.filter(field => !mapping[field]);
        const missingDateAndEvent = !mapping['date'] && !mapping['event'];
        const missingLocationAndCoords = !mapping['location'] && (!mapping['lat'] || !mapping['lng']);
        
        if (missingRequired.length > 0 || missingDateAndEvent || missingLocationAndCoords) {
            const warnings = [];
            
            if (missingRequired.length > 0) {
                warnings.push(`Missing required fields: ${missingRequired.join(', ')}`);
            }
            
            if (missingDateAndEvent) {
                warnings.push('Either Date or Event field must be mapped');
            }
            
            if (missingLocationAndCoords) {
                warnings.push('Either Location or both Latitude and Longitude must be mapped');
            }
            
            return (
                <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#fdf0e1', 
                    border: '1px solid #f3c896', 
                    borderRadius: '8px', 
                    marginTop: '16px' 
                }}>
                    <Typography variant="body2" color="warning">
                        <strong>⚠️ Field Mapping Warnings:</strong>
                        {warnings.map((warning, index) => (
                            <div key={index} style={{ marginLeft: '8px' }}>• {warning}</div>
                        ))}
                        <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                            Observations with missing required data will appear as errors in the Review section.
                        </div>
                    </Typography>
                </div>
            );
        }
        return null;
    };

    const renderFieldMapping = () => {
        if (!uploadFields.length) {
            return null;
        }

        return (
            <Stack spacing={2}>
                <Grid container spacing={2}>
                    {fieldsToMapWithNotes.map((field, index) => (
                        <React.Fragment key={field.field}>
                            <Grid item xs={6} sx={{ 
                                backgroundColor: index % 2 === 0 ? 'transparent' : 'primary.50',
                                py: 1,
                                px: 2,
                                borderRadius: 1
                            }}>
                                <Typography>{field.label}</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ 
                                backgroundColor: index % 2 === 0 ? 'transparent' : 'primary.50',
                                py: 1,
                                px: 2,
                                borderRadius: 1
                            }}>
                                <Autocomplete
                                    value={mapping[field.field] || null}
                                    options={uploadFields}
                                    getOptionLabel={(option) => option}
                                    onChange={(value) => {
                                        handleChangeMapping(field.field, value);
                                    }}
                                />
                            </Grid>
                        </React.Fragment>
                    ))}
                </Grid>
                {renderFieldMappingWarnings()}
                {!isMappingComplete && (
                    <Button 
                        variant="outlined" 
                        onClick={() => {
                            setIsMappingComplete(true);
                        }}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Continue with Partial Mapping
                    </Button>
                )}
            </Stack>
        )
    }

    const findPersonByName = (value) => {
        if (!value || typeof value !== 'string') return null;

        const normalizedValue = value.trim().toLowerCase();
        if (!normalizedValue) return null;

        const expandedOptionPersonNameList = [];

        personList.forEach((personName) => {
            const fullName = personName.get('name');
            if (!fullName) return;

            const fullNameLower = fullName.toLowerCase();
            const nameParts = fullNameLower.split(' ').filter(part => part.length > 0);

            expandedOptionPersonNameList.push({
                name: fullNameLower,
                personName,
            });

            if (nameParts.length > 1) {
                const firstName = nameParts[0];
                const lastName = nameParts[nameParts.length - 1];

                expandedOptionPersonNameList.push({
                    name: `${firstName} ${lastName}`,
                    personName,
                });
                expandedOptionPersonNameList.push({
                    name: `${firstName[0]}. ${lastName}`,
                    personName,
                });
                expandedOptionPersonNameList.push({
                    name: `${lastName}, ${firstName}`,
                    personName,
                });
                expandedOptionPersonNameList.push({
                    name: `${lastName}, ${firstName[0]}.`,
                    personName,
                });

                if (nameParts.length > 2) {
                    const middleInitials = nameParts.slice(1, -1).map(name => `${name[0]}.`).join(' ');
                    expandedOptionPersonNameList.push({
                        name: `${firstName[0]}. ${middleInitials} ${lastName}`,
                        personName,
                    });
                    expandedOptionPersonNameList.push({
                        name: `${firstName} ${middleInitials} ${lastName}`,
                        personName,
                    });
                    expandedOptionPersonNameList.push({
                        name: `${lastName}, ${firstName[0]}. ${middleInitials}`,
                        personName,
                    });
                }
            }
        })

        const match = expandedOptionPersonNameList.find(({ name }) => name.toLowerCase() === normalizedValue);
        return match?.personName || null;
    }
            

    const parseField = async (field, value) => {
        if (field.type.type === 'Pointer') {
            if (field.type.targetClass === 'Person') {
                let person = findPersonByName(value);
                if (!person && value && value.trim()) {
                    const searchResult = await dispatch(lookupTableActions.searchLookupTable('PersonName', value, 10, 0));
                    if (searchResult.value?.results?.length > 0) {
                        person = searchResult.value.results.find((item) =>
                            item?.get('name')?.toLowerCase() === value?.toLowerCase()
                        );
                    }
                }
                return person || null;
            } else if (field.type.targetClass === 'Name') {
                const classArrayIdentifier = classMap[field.type.targetClass];
                const classArray = observationOptions[classArrayIdentifier];
                let match = classArray.find((item) => 
                    item?.get('name')?.toLowerCase() === value?.toLowerCase()
                );
                
                if (!match && value && value.trim()) {
                    const searchResult = await dispatch(lookupTableActions.searchLookupTable('Name', value, 10, 0));
                    if (searchResult.value?.results?.length > 0) {
                        match = searchResult.value.results.find((item) => 
                            item?.get('name')?.toLowerCase() === value?.toLowerCase()
                        );
                    }
                }
                
                return match || null;
            } else if (field.type.targetClass === 'Event') {
                const classArrayIdentifier = classMap[field.type.targetClass];
                const classArray = observationOptions[classArrayIdentifier];
                let match = classArray?.find((item) =>
                    item?.get('name')?.toLowerCase() === value?.toLowerCase()
                );

                if (!match && value && value.trim()) {
                    const searchResult = await dispatch(lookupTableActions.searchLookupTable(field.type.targetClass, value, 10, 0));
                    if (searchResult.value?.results?.length > 0) {
                        match = searchResult.value.results.find((item) =>
                            item?.get('name')?.toLowerCase() === value?.toLowerCase()
                        );
                    }
                }

                return match || null;
            } else {
                const classArrayIdentifier = classMap[field.type.targetClass];
                const classArray = observationOptions[classArrayIdentifier];
                let match = classArray?.find((item) =>
                    item?.get('label')?.toLowerCase() === value?.toLowerCase()
                );

                if (!match && value && value.trim()) {
                    const searchResult = await dispatch(lookupTableActions.searchLookupTable(field.type.targetClass, value, 10, 0));
                    if (searchResult.value?.results?.length > 0) {
                        match = searchResult.value.results.find((item) =>
                            item?.get('label')?.toLowerCase() === value?.toLowerCase()
                        );
                    }
                }

                return match || null;
            }
        } else if (field.type === 'Boolean') {
            const truthyValues = ['true', 'yes', '1', 't', 'y'];
            return truthyValues.includes(value.toLowerCase());
        } else if (field.type === 'Date') {
            return parseDate(value);
        } else if (field.type === 'Number') {
            if (!value || value.trim() === '') {
                return null;
            }
            const parsed = parseFloat(value);
            return isNaN(parsed) ? value : parsed;
        } else {
            return value;
        }
    }

    const createObservationForSave = (observation) => {
        const newObservation = new Observation();
        fieldsToMap.forEach((field) => {
            if (field.type.type === 'Pointer') {
                if (field.type.targetClass === 'Person') {
                    newObservation.set(field.field, observation.get(field.field)?.get('person')?.id);
                } else {
                    newObservation.set(field.field, observation.get(field.field)?.id);
                }
            } else {
                newObservation.set(field.field, observation.get(field.field));
            }
        });
        return newObservation;
    }

    const observationSave = async (observation, notes = []) => {
        const observationToSave = createObservationForSave(observation);
        const savedObservationRequest = await dispatch(observationActions.save(observationToSave));
        const savedId = savedObservationRequest.value.id;
        
        if (notes && notes.length > 0) {
            await Promise.all(notes.map(note => 
                dispatch(noteActions.save({
                    observation: savedId,
                    type: note.type.id,
                    text: note.text
                }))
            ));
        }
        
        const fullObservation = await dispatch(observationActions.get(savedId));
        return fullObservation.value;
    };

    const downloadFile = (blob, filename) => {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        }
    };

    const downloadErroredRows = (erroredFieldOptions, erroredRows) => {
        const today = new Date();
        const dateStr = today.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');
    
        const failedObservationsCSV = Papa.unparse(erroredRows);
        const failedBlob = new Blob([failedObservationsCSV], { type: 'text/csv;charset=utf-8' });
        downloadFile(failedBlob, `${dateStr}-failed-boriis-upload-observations.csv`);
    
        const fieldOptionsColumns = {};
        erroredFieldOptions.forEach(fieldOption => {
            Object.entries(fieldOption).forEach(([field, values]) => {
                if (Array.isArray(values)) {
                    fieldOptionsColumns[field] = values;
                }
            });
        });
    
        const maxLength = Math.max(...Object.values(fieldOptionsColumns).map(arr => arr.length));
    
        const fieldOptionsRows = Array.from({ length: maxLength }, (_, i) => {
            const row = {};
            Object.entries(fieldOptionsColumns).forEach(([field, values]) => {
                row[field] = values[i] || '';
            });
            return row;
        });
        if (fieldOptionsRows.length > 0) {
            const fieldOptionsCSV = Papa.unparse(fieldOptionsRows);
            const optionsBlob = new Blob([fieldOptionsCSV], { type: 'text/csv;charset=utf-8' });
            setTimeout(() => {
                downloadFile(optionsBlob, 'boriis-field-options.csv');
            }, 100);
        }
    };
      

    const finalize = async (editedObservations = []) => {
        const erroredCols = []
        const erroredRows = []
        erroredObservations.forEach(({ row, errors }) => {
            erroredRows.push(row);
            Object.keys(errors).forEach((key) => {
                if (!erroredCols.includes(key)) {
                    erroredCols.push(key);
                }
            });
        });

        const erroredFieldOptions = [];
        erroredCols.forEach((col) => {
            const erroredFieldData = fieldsToMap.find((field) => field.field === col);

            if (erroredFieldData?.type?.type === 'Pointer') {
                let options
                if (erroredFieldData.type.targetClass === 'Person') {
                    options = personList.map((personName) => {
                        const person = personName.get('person');
                        return person.get('firstName') + ' ' + person.get('lastName')
                    });
                } else {
                    const classArrayIdentifier = classMap[erroredFieldData.type.targetClass];
                    const classArray = observationOptions[classArrayIdentifier];
                    options = classArray.map((item) => item.get('label'));
                }
                erroredFieldOptions.push({
                    [col]: options
                });
            }
        })
        if (erroredObservations.length > 0 && editedObservations.length === 0) {
            downloadErroredRows(erroredFieldOptions, erroredRows);
        }

        const allObservationsToSave = [
            ...observations.map(({ observation, notes }) => ({ observation, notes })),
            ...editedObservations.map(observation => ({ observation, notes: [] }))
        ];

        await Promise.all(allObservationsToSave.map(({ observation, notes }) => 
            observationSave(observation, notes)
        ));
        navigate('/');
    }

    const handleReview = async () => {
        setIsProcessing(true);

        const mappedObservations = await Promise.all(uploadData.map(async (row) => {
            const observation = new Observation();
            const notes = [];

            for (const field of fieldsToMap) {
                const mappedFieldName = mapping[field.field];
                if (mappedFieldName && row[mappedFieldName] !== undefined) {
                    const fieldValue = await parseField(field, row[mappedFieldName]);
                    observation.set(field.field, fieldValue);
                }
            }

            for (const noteType of observationNoteTypes) {
                const fieldKey = `note_${noteType.id}`;
                const mappedFieldName = mapping[fieldKey];
                if (mappedFieldName && row[mappedFieldName] && row[mappedFieldName].trim()) {
                    notes.push({
                        type: noteType,
                        text: row[mappedFieldName].trim()
                    });
                }
            }

            try {
                const observationToSave = createObservationForSave(observation);
                await observationToSave.inspect();
                return {
                    observation: observation,
                    notes: notes,
                    row
                }
            } catch (err) {
                const observationErrors = err.inner?.reduce((acc, curr) => {
                    const fieldName = curr.path;
                    const mappedField = mapping[fieldName];
                    const originalValue = mappedField ? row[mappedField] : null;
                    acc[fieldName] = originalValue || curr.message;
                    return acc;
                }, {});
                return {
                    observation,
                    notes: notes,
                    row,
                    errors: observationErrors
                }
            }
        }))

        const successful = mappedObservations.filter(({ errors }) => !errors);
        setObservations(successful);
        const errored = mappedObservations.filter(({ errors }) => errors);
        setErroredObservations(errored);
        setIsProcessing(false);
    }

    return (
        <Sheet sx={{ p: 4 }}>
            <ProcessingOverlay enabled={isProcessing} caption="Matching observations..." />
            <Stack spacing={4}>
                <Typography variant="h4">Import Observations</Typography>
                <Accordion expanded={expanded === 'upload'} onChange={handleChangeAccordion('upload')}>
                    <AccordionSummary>Upload CSV</AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2} sx={{p: 2}}>
                            {renderUpload()}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                <Accordion 
                    expanded={expanded === 'mapping'} 
                    onChange={handleChangeAccordion('mapping')}
                    disabled={!uploadFields.length}
                >
                    <AccordionSummary>Field Mapping</AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2} sx={{p: 2}}>
                            {renderFieldMapping()}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'review'}
                    onChange={handleChangeAccordion('review')}
                    disabled={!isMappingComplete}
                >
                    <AccordionSummary>Review</AccordionSummary>
                    <AccordionDetails>
                        <ImportedObservations
                            observations={observations}
                            erroredObservations={erroredObservations}
                            mapping={mapping}
                            finalize={finalize}
                        />
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Sheet>
    );
};

export default ObservationImport;